/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    DomainToken,
    GetScopedDomainTokenRequest,
} from 'client-code/domain-tokens';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device';
import { SlsServiceGlobalEndpointsService } from '../infrastructure/SlsServiceGlobalEndpointsService';
import { IDomainTokensServiceRepository } from '../interfaces/IDomainTokensServiceRepository';
import { Log } from '../logging';
import { Storage } from '../storage/Storage';
import { TServicesParams } from '../types';
import { TDomainTokensResponse } from '../types/TDomainTokensResponse';

export class DomainTokensServiceRepositoryImpl implements IDomainTokensServiceRepository {
    private static TAG: string = 'DomainTokensServiceRepositoryImpl';

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;
    }

    public async fetchScopedDomainTokens(
        domain: string,
        authorization: string,
    ): Promise<TDomainTokensResponse> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.PLAYER).headers;
            const configurationParameters: ConfigurationParameters = {
                basePath: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                    SlsServiceGlobalEndpointsService.DOMAIN_TOKENS,
                ),
                apiKey: this.mServiceParams.accessToken,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };
            const configuration = new Configuration(configurationParameters);
            const scopedDomainTokenApi = new DefaultApi(configuration);
            const requestParams: GetScopedDomainTokenRequest = {
                domain,
                authorization: `${authorization} ${DeviceModel.getAccessToken()}`,
            };
            const scopedDomainTokenResponse =
                await scopedDomainTokenApi.getScopedDomainToken(requestParams);
            const combinedParams = `${domain}#${authorization}`;

            let scopeDomainTokens = Storage.getInstance().getItem(Storage.Keys.SCOPE_DOMAIN_TOKENS);

            if (scopeDomainTokens !== null) {
                scopeDomainTokens = {
                    ...scopeDomainTokens,
                    [combinedParams]: scopedDomainTokenResponse.data,
                };
            } else {
                scopeDomainTokens = { [combinedParams]: scopedDomainTokenResponse.data };
            }
            Storage.getInstance().setItem(Storage.Keys.SCOPE_DOMAIN_TOKENS, scopeDomainTokens);

            return {
                domainToken: scopedDomainTokenResponse.data,
            };
        } catch (error) {
            Log.e(DomainTokensServiceRepositoryImpl.TAG, 'fetchScopedDomainTokens', error);
            return {
                domainToken: {} as DomainToken,
                hasError: true,
                errorCode: (error as Error)?.message || 'Unknown error',
            };
        }
    }
}
